import React from 'react';
import { motion } from 'motion/react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const TermsOfService = () => {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50">
        <div className="container mx-auto max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Terms of <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Service</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Last Updated: January 29, 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600 mb-6">
              By accessing or using the Goodikids website or mobile application (“Service”), you agree to these Terms of Service. If you do not agree, please stop using the Service immediately.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">2. Eligibility & Parental Responsibility</h2>
            <p className="text-gray-600 mb-4">
              Goodikids is designed for children, but only parents/guardians (18+) may create and manage accounts.
            </p>
            <p className="text-gray-600 mb-4">
              By using the Service, you confirm that:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>You are the parent or legal guardian</li>
              <li>You supervise your child’s app usage</li>
              <li>You take responsibility for all activities under your account</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Children may use the app only under adult supervision.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">3. User Accounts</h2>
            <p className="text-gray-600 mb-4">
              When creating an account, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Provide accurate information</li>
              <li>Keep your login details secure</li>
              <li>Notify us of unauthorized access</li>
              <li>Accept responsibility for activity under your account</li>
            </ul>
            <p className="text-gray-600 mb-6">
              We may suspend or terminate accounts that violate these Terms.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">4. Subscriptions & Payments</h2>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">A. Free Access</h3>
            <p className="text-gray-600 mb-6">
              Some courses, subtopics, and features are free to use.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">B. Premium Access (Course-Based)</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Premium access is course-wise, not full-app access.</li>
              <li>Each course must be purchased separately, and purchasing one course does not unlock all courses.</li>
              <li>All sub-courses within a purchased course unlock automatically.</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">C. Billing & Renewal</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Prices vary by course and region</li>
              <li>Subscription renewals are handled by Google Play or Apple App Store</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">D. Refunds</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Refunds are handled exclusively by the App Store or Play Store.</li>
              <li>Goodikids cannot issue refunds directly.</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">5. Acceptable Use</h2>
            <p className="text-gray-600 mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Use the Service for illegal activities</li>
              <li>Hack, disrupt, or damage the platform</li>
              <li>Share or transfer your account</li>
              <li>Copy or distribute our content</li>
              <li>Upload harmful or inappropriate material</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Violation may result in account termination.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">6. Intellectual Property</h2>
            <p className="text-gray-600 mb-6">
              All content on Goodikids—including images, animations, MCQs, audio, certificates, icons, and designs—is owned by Goodikids and protected by law. You may not reproduce or modify any content without permission.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">7. Educational Disclaimer</h2>
            <p className="text-gray-600 mb-4">
              Goodikids provides general educational content to support learning and values development.
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>It does not replace teachers, doctors, therapists, or professional services.</li>
              <li>Learning outcomes are not guaranteed.</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">8. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              Goodikids is provided “as is” and “as available.” We are not responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Data loss or device issues</li>
              <li>Service interruptions</li>
              <li>Indirect or incidental damages</li>
              <li>Any decisions or actions taken based on app content</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Use of the Service is at your own risk.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">9. Termination</h2>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>We may suspend or terminate accounts that violate these Terms.</li>
              <li>Parents may request account deletion at any time.</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">10. Governing Law</h2>
            <p className="text-gray-600 mb-6">
              These Terms are governed by the laws of India. Disputes will be resolved in the courts of [Delhi,India].
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">11. Dispute Resolution</h2>
            <p className="text-gray-600 mb-4">
              Before taking legal action, you agree to:
            </p>
            <ol className="list-decimal pl-6 text-gray-600 mb-6 space-y-2">
              <li>Contact us at legal@goodikids.com</li>
              <li>Allow up to 30 days for us to respond</li>
            </ol>
            <p className="text-gray-600 mb-6">
              Class-action lawsuits are not permitted.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">12. Changes to Terms</h2>
            <p className="text-gray-600 mb-6">
              We may update these Terms from time to time. Continued use of the Service means you accept the updated Terms.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">13. Contact Information</h2>
            <p className="text-gray-600 mb-6">
              For questions about these Terms:
              <br />
              📧 Email: <a href="mailto:legal@goodikids.com" className="text-orange-500 hover:text-orange-600">legal@goodikids.com</a>
              <br />
              🌐 Website: <a href="https://www.goodikids.com/" className="text-orange-500 hover:text-orange-600">https://www.goodikids.com/</a>
            </p>
            <p className="text-gray-500 text-sm mt-12">
              ©️ 2025 Goodikids. All rights reserved.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;